import {
	createRouter,
	createWebHashHistory,
	RouteRecordRaw,
	Router,
	RouteLocationNormalized
} from 'vue-router'
import { judge_token } from '@/utils/judgeIdentity'
import { ElMessage } from 'element-plus'
import { t } from '@/locales'
import 'nprogress/nprogress.css'
import NProgress from 'nprogress'

NProgress.configure({
	showSpinner: false // 是否显示加载ico
})
/**
 * 公开路由配置
 * 定义无需登录即可访问的路由路径
 */
const publicRoutes: string[] = [
	'/',
	'/portal',
	'/portal/home',
	'/agreement',
	'/h5/scan-upload',
	'/portal/H5share',
	'/portal/share'
]
/**
 * 检查路由是否为公开路由
 * @param path 路由路径
 * @returns 是否为公开路由
 */
export const isPublicRoute = (path = ''): boolean => {
	return publicRoutes.some(route => {
		// 完全匹配
		if (path === route) return true

		// 如果路由包含通配符，使用正则匹配
		if (route.includes('*')) {
			return new RegExp(route.replace('*', '.*')).test(path)
		}

		// 对于 /portal 这样的路由，只匹配 /portal 和 /portal/home
		if (route === '/portal') {
			return path === '/portal' || path === '/portal/home'
		}

		return false
	})
}

/**
 * 路由元数据接口
 */
export interface RouteMeta {
	title?: string // 页面标题
	public?: boolean // 是否为公开路由
	noLayout?: boolean // 是否不使用主布局
	requiresAuth?: boolean // 是否需要认证
}

/**
 * 路由配置
 */
const routes: RouteRecordRaw[] = [
	// 重定向到门户首页
	{
		path: '/',
		redirect: '/portal'
	},

	// 门户页面
	{
		path: '/portal',
		component: () => import('@/views/portal/index.vue'),
		redirect: '/portal/home',
		children: [
			{
				path: 'home',
				component: () => import('@/views/portal/home/index.vue'),
				name: t('portal.homePage')
			},
			{
				path: 'myPortfolio/:id',
				component: () => import('@/views/portal/MyPortfolio/index.vue'),
				name: t('portal.WorksLibrary')
			},
			{
				path: 'userInfo',
				component: () => import('@/views/portal/UserInfo/index.vue'),
				name: t('portal.personalCenter')
			},
			// 声库
			{
				path: '/soundBank',
				component: () => import('@/views/soundBank/index.vue'),
				name: t('portal.soundBank')
			},
			// 扫码分享PC页面
			{
				path: '/portal/share',
				component: () => import('@/views/portal/share/index.vue'),
				name: t('portal.share')
			},

			{
				path: '/ImageLibrary',
				component: () => import('@/views/ImageLibrary/index.vue'),
				name: t('portal.ImageLibrary')
			}
		]
	},
	// 视频编辑
	{
		path: '/videoEdit',
		component: () => import('@/views/YoyaEdit/index.vue'),
		name: t('portal.instantVideoCreation')
	},

	// 素材库
	{
		path: '/material',
		component: () => import('@/views/MaterialLibrary/index.vue'),
		name: t('portal.MaterialLibrary')
	},

	// AI音乐
	{
		path: '/aiMusic',
		component: () => import('@/views/AIMusic/index.vue'),
		name: t('portal.AImusic')
	},

	// 素材成片
	{
		path: '/materialSelect',
		component: () => import('@/views/MaterialSelect/index.vue'),
		name: t('portal.materialVideo')
	},

	// AI视频
	{
		path: '/aiCreateVideo',
		component: () => import('@/views/AiCreateVideo/index.vue'),
		name: t('portal.AIvideo')
	},

	// 视频转译
	{
		path: '/videoTranslate',
		component: () => import('@/views/VideoTranslate/index.vue'),
		name: t('portal.videoTranslationAgent')
	},

	// 图文速编
	{
		path: '/reporterInterviewAi',
		component: () => import('@/views/reporterInterviewAi/index.vue'),
		name: t('portal.journalistWritingAgent')
	},
	// 视频超分
	{
		path: '/videoUpscaler',
		component: () => import('@/views/VideoUpscaler/index.vue'),
		name: t('portal.videoUpscaler')
	},
	// 风格迁移
	{
		path: '/imgStyleTransfer',
		component: () => import('@/views/imgStyleTransfer/index.vue'),
		name: t('portal.imgStyleTransfer')
	},
	// 风格迁移
	{
		path: '/restorationPhoto',
		component: () => import('@/views/restorationPhoto/index.vue'),
		name: t('portal.restorationPhoto')
	},
	// 视频拆条
	{
		path: '/videoSplitter',
		component: () => import('@/views/VideoSplitter/index.vue'),
		name: t('portal.videoClipsAgent')
	},
	// 数字人视频
	{
		path: '/DigitalHumanVideo',
		component: () => import('@/views/DigitalHumanVideo/index.vue'),
		name: t('portal.DigitalHumanVideo')
		// name: "数字人视频"
	},

	// 用户协议
	{
		path: '/agreement/:id',
		component: () => import('@/views/agreement/index.vue'),
		name: '优雅AI创作平台用户服务协议',
		props: true,
		meta: {
			public: true // 标记为公开路由
		}
	},

	// H5扫码上传页面
	{
		path: '/h5/scan-upload',
		component: () => import('@/views/H5/H5ScanUpload/index.vue'),
		name: 'H5扫码上传',
		meta: {
			title: 'YOYA视频转译 - 文件上传',
			public: true, // 标记为公开路由
			noLayout: true // 不使用主布局
		}
	},
	{
		path: '/h5/error',
		component: () => import('@/views/H5/error.vue'),
		name: 'H5失效',
		meta: {
			title: 'H5失效',
			public: true, // 标记为公开路由
			noLayout: true // 不使用主布局
		}
	},

	// 扫码分享H5页面
	{
		path: '/portal/H5share',
		component: () => import('@/views/portal/share/H5share.vue'),
		meta: {
			public: true // 标记为公开路由
		}
	},
	// 新手指引
	{
		path: '/portal/beginnerGuide',
		component: () => import('@/views/portal/beginnerGuide/index.vue'),
		meta: {
			public: true // 标记为公开路由
		}
	},
	// 声音迁移
	{
		path: '/VoiceClone',
		component: () => import('@/views/VoiceClone/index.vue'),
		name: t('portal.VoiceClone')
	}
]

/**
 * 创建路由实例
 */
const router: Router = createRouter({
	history: createWebHashHistory(),
	routes
})

/**
 * 检查用户认证状态
 * @returns 用户是否已认证
 */
const checkAuthentication = (): boolean => {
	// 首先检查 loginState
	if (localStorage.getItem('loginState') === '1') {
		return true
	}

	// 然后检查 userInfo 和 userHash
	return judge_token(localStorage.getItem('userInfo'), localStorage.getItem('userHash'))
}

/**
 * 处理未认证情况
 * @param next 路由导航函数
 */
const handleUnauthenticated = (next: Function): void => {
	ElMessage.error(t('请先登录'))
	// 清除所有登录相关的存储
	localStorage.removeItem('userInfo')
	localStorage.removeItem('userHash')
	localStorage.removeItem('loginState')
	localStorage.removeItem('userId')
	localStorage.removeItem('nickName')
	next('/')
}

/**
 * 路由守卫
 * 重新设计的路由守卫，将逻辑分解为更小的函数以提高可维护性
 */
router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: Function) => {
	NProgress.start()
	// 检查路由是否是公开路由或带有public标记
	const isPublic = isPublicRoute(to.path) || (to.meta && to.meta['public'] === true)
	// 检查用户是否已认证
	const isAuthenticated = checkAuthentication()
	// 如果是公开路由，直接访问
	if (isPublic) {
		return next()
	}
	// 根据认证状态决定路由
	if (isAuthenticated) {
		next() // 用户已认证，允许访问请求的路由
	} else {
		handleUnauthenticated(next) // 用户未认证，重定向到首页
	}
})
router.afterEach(() => {
	NProgress.done()
})

export default router
